// Story 7.2 (ADO-905), AC1/AC3/AC7 — product keyword-search screen. A search
// bar (the Figma "Mega Menu - Browse" carries one) drives a ranked partial-text /
// fuzzy search, rendering the Story 7.1 ProductCard for each result row. A
// zero-results search shows an explicit empty state that ECHOES the query (AC3),
// never an error surface — a genuine transport failure is a separate Banner
// (mirrors the Browse empty-vs-error split). Add-to-basket from a result is a
// documented seam only (AC7): ProductCard renders no basket write here.
//
// Presentational + data-fetching only; the auth gate lives on the /search route
// in main.tsx (mirrors BrowseRoute). Search is submit-driven (Enter / button) so
// each result set corresponds to a query the user deliberately ran. The last
// good result set is KEPT visible through a re-search and through a transient
// failure (the browse "keep listing during refetch" posture, 0cff272); a
// request-sequence guard drops out-of-order settlements so a slow response can
// never overwrite a newer one.
//
// Epic 7 pixel parity (ADO-903): the results body wears the SAME re-skinned
// listing chrome as CategoryListing — pinned CollapsibleFilterSidebar rail
// (with ad placeholder), toolbar row (Filters switch / drawer button ·
// breadcrumb-style Shop / “term” crumb · count + grid/list view toggle), chips
// row, and the shared mobile FiltersDrawer. The listing chrome CSS is imported
// from CategoryListing.module.css so the two surfaces stay in lock-step.
// Search is a single capped page (no cursor), so it renders NO pagination
// footer; the Story 7-FU-C truncation notice (AC6) keeps its exact text +
// role="status" semantics, restyled as a subtle inline note.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  AppShell,
  Banner,
  Button,
  CollapsibleFilterSidebar,
  FormField,
  Icon,
} from '../../design-system';
import {
  MAX_SEARCH_QUERY_LENGTH,
  searchProducts,
  type ProductSearchResult,
} from '../../api/search.client';
import {
  hasActiveFilters,
  filtersKeyOf,
  sameFilters,
  type FacetKey,
  type ProductFilters,
} from '../../api/facets';
import { useFilterPanel } from '../../api/useFilterPanel';
import { useAvailability } from '../../availability/useAvailability';
import { usePrices } from '../../pricing/usePriceStore';
import { useMultibuyDeals } from '../../multibuy/useMultibuyDealsStore';
import { useCatalogueEntryKey } from '../../ordering/catalogueEntry';
import {
  resolvesFeaturedLines,
  useCatalogueReadAccountCode,
} from '../../ordering/catalogueReadAccount';
import { useOnOrder } from '../../onOrder/useOnOrder';
import { DepotPicker } from '../../availability/DepotPicker';
import { FilterChips } from '../Browse/FilterChips';
import { FilterPanel } from '../Browse/FilterPanel';
import { removeCoupledFilterValue, toggleCoupledFilterValue } from '../Browse/rbpFilterCoupling';
import { FiltersDrawer } from '../Browse/FiltersDrawer';
import { ProductCard } from '../Browse/ProductCard';
import { useViewMode } from '../Browse/useViewMode';
// Shared listing chrome (shell / sidebar / toolbar / chips / grid / list) —
// imported from CategoryListing so search results and the category listing
// cannot drift apart visually (Epic 7 pixel parity, ADO-903).
import listing from '../Browse/CategoryListing.module.css';
import crumbs from '../Browse/Breadcrumb.module.css';
import styles from './ProductSearch.module.css';

type Phase = 'idle' | 'loading' | 'error' | 'ready';

/** A stable empty-facets reference for controlled mode (effect-dependency safe). */
const EMPTY_FILTERS: ProductFilters = Object.freeze({});

// Story 7-FU-D (ADO-1217), AC3 — mark the first row of result cards as the LCP
// candidate (eager + fetchpriority=high); every other card stays lazy. A small
// constant ≈ one visual row (D-3); real LCP timing is a Playwright/manual concern.
const EAGER_IMAGE_COUNT = 4;

/** Page mode: the standalone screen owns its search box, term and facet state. */
interface ProductSearchPageProps {
  readonly embedded?: false;
  readonly term?: undefined;
  readonly runToken?: undefined;
  readonly filters?: undefined;
  readonly onFiltersChange?: undefined;
  readonly onFiltersPrune?: undefined;
  readonly collectedOffers?: undefined;
  readonly onCollectedOffersChange?: undefined;
}

/**
 * Embedded mode (Epic 7 re-skin, ADO-903): render only the results body (no AppShell, no
 * internal search form). The `/products` SiteHeader owns the search box and drives the
 * committed term, and (Story 7.35, ADO-2032) the URL is the single source of truth for the
 * facet selection, so the host supplies the active filters and is told of every toggle,
 * clear and chip removal to write to the query string, instead of local state holding them.
 */
interface ProductSearchEmbeddedProps {
  readonly embedded: true;
  /** The committed search term (from the SiteHeader search box). */
  readonly term: string;
  /**
   * A monotonic token that changes on every submit (even for an identical term),
   * so re-submitting the same query forces a re-run (page-mode parity with
   * `submitNonce`).
   */
  readonly runToken?: number;
  /** The active attribute filters, from the URL. */
  readonly filters: ProductFilters;
  /** Report a new facet selection for the host to write to the URL. */
  readonly onFiltersChange: (filters: ProductFilters) => void;
  /** Story 7.29 (ADO-2025, D-7.29-37): report the selection a search applied, once it answered
   *  the selection on screen, for the host to write to the URL with a replace. */
  readonly onFiltersPrune?: (applied: ProductFilters) => void;
  /** Story 7.31 (ADO-1113), AC1 (PR #862 review): search within the collected offers
   *  (`/products?offers=all`), so the results are the collection's, as the URL says. */
  readonly collectedOffers?: boolean;
  /** Story 7.31 (ADO-1113): the rail's "On Offer" checkbox and its chip turn the collection on
   *  or off, for the host to write to the URL. */
  readonly onCollectedOffersChange?: (on: boolean) => void;
}

export type ProductSearchProps = ProductSearchPageProps | ProductSearchEmbeddedProps;

export function ProductSearch({
  embedded = false,
  term,
  runToken,
  filters: controlledFilters,
  onFiltersChange,
  onFiltersPrune,
  collectedOffers = false,
  onCollectedOffersChange,
}: ProductSearchProps = {}) {
  const [query, setQuery] = useState('');
  // Story 7.35 (ADO-2032): in embedded mode the host drives the term and the facets
  // through the URL, so `controlled` is the embedded mode: the active filters come from
  // the prop and mutations are reported upward rather than held locally. The props type
  // requires both facet props whenever `embedded` is set.
  const controlled = embedded;
  // The last successfully loaded result set (carries the echoed query for AC3).
  // Retained through 'loading' and 'error' so a re-search or a transient
  // failure never blanks a result set the user is looking at.
  const [result, setResult] = useState<ProductSearchResult | null>(null);
  // Story 7.29 (ADO-2025, D-7.29-29, D-7.29-37): the selection and the account the current
  // `result` answered. The gates read the account, so its echo and narrowings stand only for it.
  const [answeredFiltersKey, setAnsweredFiltersKey] = useState<string | null>(null);
  const [answeredAccountCode, setAnsweredAccountCode] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  // Story 7.10 (ADO-906): the term that produced the current result (so a filter
  // toggle can re-run the SAME search with the new filters, AC1/AC3), the active
  // attribute filters, a submit nonce (forces a re-search on re-submitting the
  // same term), and the panel toggle (desktop rail) + drawer state (<1024px).
  const [submittedTerm, setSubmittedTerm] = useState('');
  const [submitNonce, setSubmitNonce] = useState(0);
  const [filters, setFilters] = useState<ProductFilters>({});
  // The effective facet selection: the controlled prop when the host owns it
  // (URL-driven), otherwise the local state (page mode).
  const activeFilters = controlled ? (controlledFilters ?? EMPTY_FILTERS) : filters;
  // Story 7.35 (ADO-2032): in controlled mode the host commits a new term and drops its facets
  // in ONE URL write, so the runner reads the term and the run token straight from the props.
  // Copying them into state first (as page mode's form does) would let the runner fire once
  // with the old term and the new, empty facets before the copy lands: an extra request.
  const runTerm = controlled ? (term ?? '').trim() : submittedTerm;
  const runNonce = controlled ? (runToken ?? 0) : submitNonce;
  // The LATEST selection, readable synchronously: the single-select facets toggle twice in one
  // handler (drop the old value, add the new one), and the second call must see the first. A
  // settling search also reads it to tell whether it answered this selection (D-7.29-37).
  const latestFiltersRef = useRef(activeFilters);
  latestFiltersRef.current = activeFilters;
  // Story 7.29 (ADO-2025, D-7.29-37): the host's prune writer, read when a search settles.
  const onFiltersPruneRef = useRef(onFiltersPrune);
  onFiltersPruneRef.current = onFiltersPrune;
  // Enabler E7-C (ADO-2274), D-1: the selected ordering entry rides the search: the server
  // searches that entry's range, and shows a discontinued exact hit (Story 8.2, ADO-2011, AC6)
  // only when it has a live substitute at the entry's depot. An entry change re-runs the search.
  const catalogueEntryKey = useCatalogueEntryKey();
  // Story 7.29 (ADO-2025), D-7.29-21: the active account rides the search for the narrowing gates.
  // Story 7.32 (ADO-1115): within the Featured Lines or the collected offers it is the account the
  // listing prices them for, by the rule the listing shares (`catalogueReadAccount.ts`; none for
  // staff).
  const catalogueAccountCode = useCatalogueReadAccountCode(
    resolvesFeaturedLines(collectedOffers, activeFilters),
  );
  const { showFilters, toggleFilters } = useFilterPanel();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Epic 7 re-skin (ADO-903): grid/list view toggle, opening in the grid view at
  // every width (shared with CategoryListing).
  const { viewMode, selectView } = useViewMode();
  // Monotonic submit counter — a settlement whose sequence is no longer current
  // is stale (a newer submit/filter-change superseded it) and is discarded.
  const seqRef = useRef(0);
  // Story 7-FU-D (ADO-1217), AC2 — the in-flight request's controller, so a new
  // run (or an unmount) can ABORT the previous request on the wire rather than
  // letting N superseded searches run to completion server-side.
  const abortRef = useRef<AbortController | null>(null);
  // Story 7.29 (ADO-2025, D-7.29-37): the search the result on screen answered, null while one is in
  // flight (a re-run aborts it) or after a failure. A run that would ask what is already answered
  // (a pruned selection) asks nothing; a new term, a re-submit, an entry or an account change is a
  // different search.
  const answeredSearchKeyRef = useRef<string | null>(null);

  // The single search runner (AC1/AC3): the raw FTS candidate set is filtered +
  // faceted server-side; the seq guard drops out-of-order settlements so a slow
  // response can never overwrite a newer one. Driven ONLY by the effect below so
  // submit and filter-change share one path.
  useEffect(() => {
    if (runTerm === '') return;
    // With no entry there is nothing to search; the effect re-runs when one is chosen. The products
    // page keys its embedded search by the entry, so there it remounts empty; this branch drops
    // the previous range's results for a search that stays mounted (page mode).
    if (catalogueEntryKey === null) {
      ++seqRef.current;
      abortRef.current?.abort();
      answeredSearchKeyRef.current = null;
      setResult(null);
      setPhase('idle');
      return;
    }
    const searchKeyFor = (selection: ProductFilters) =>
      JSON.stringify([
        runTerm,
        filtersKeyOf(selection),
        runNonce,
        catalogueEntryKey,
        catalogueAccountCode,
        collectedOffers,
      ]);
    if (searchKeyFor(activeFilters) === answeredSearchKeyRef.current) return;
    answeredSearchKeyRef.current = null;
    const seq = ++seqRef.current;
    // AC2 — abort the previous in-flight request before starting a new one, then
    // arm a fresh controller for this run. The effect cleanup aborts too (covers
    // unmount + a dep-change supersede); the seq guard below stays the
    // belt-and-braces for any settlement that still resolves after the abort.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setPhase('loading');
    searchProducts(
      catalogueEntryKey,
      runTerm,
      activeFilters,
      controller.signal,
      catalogueAccountCode,
      collectedOffers,
    )
      .then((next) => {
        if (seq !== seqRef.current) return; // stale response — a newer search ran
        setResult(next);
        setAnsweredAccountCode(catalogueAccountCode);
        setPhase('ready');
        // D-7.29-37: a search that answered the current selection prunes it to what it applied,
        // so a member the server ignored is neither held nor sent again. The server ignored the
        // dropped members, so this answer already is the pruned selection's: it is recorded as
        // answered, and the prune searches nothing.
        // In controlled mode the host writes the pruned selection to the URL with a replace.
        const applied = next.appliedFilters;
        answeredSearchKeyRef.current = searchKeyFor(activeFilters);
        if (
          applied !== undefined &&
          sameFilters(latestFiltersRef.current, activeFilters) &&
          !sameFilters(applied, activeFilters)
        ) {
          answeredSearchKeyRef.current = searchKeyFor(applied);
          latestFiltersRef.current = applied;
          setAnsweredFiltersKey(filtersKeyOf(applied));
          if (controlled) onFiltersPruneRef.current?.(applied);
          else setFilters(applied);
        } else {
          setAnsweredFiltersKey(filtersKeyOf(activeFilters));
        }
      })
      .catch((err: unknown) => {
        // AC2 — an aborted request is NOT a failure: it was superseded or the
        // screen unmounted. Never trip the error Banner (reserved for a genuine
        // transport/non-2xx failure); leave the current phase to the newer run.
        if (controller.signal.aborted || (err as { name?: string } | null)?.name === 'AbortError') {
          return;
        }
        if (seq !== seqRef.current) return; // stale failure — keep the newer state
        setPhase('error'); // keep the last good `result` visible under the banner
      });
    return () => {
      controller.abort();
    };
  }, [
    runTerm,
    activeFilters,
    runNonce,
    catalogueEntryKey,
    catalogueAccountCode,
    collectedOffers,
    controlled,
  ]);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const term = query.trim();
    if (term === '') return; // a blank submit is a no-op — nothing to search
    // Story 7.10 code-review (2026-07-13): reset attribute filters ONLY on a NEW
    // search term (mirrors Browse's per-node reset). Otherwise a stale filter from
    // the previous term is applied to the new one — an empty result for a term that
    // actually has matches, plus an orphan chip the recomputed facets can't offer.
    // Guarded on term change so re-submitting the SAME query (Enter again to
    // refresh) keeps the active filters rather than silently wiping them.
    if (term !== submittedTerm) setFilters({});
    setSubmittedTerm(term);
    setSubmitNonce((n) => n + 1); // re-run even when the term is unchanged
  };

  // Story 7.3 (ADO-1139): populate the availability store (stock pill +
  // "{n} in store") for the result rows on screen; no-op when unconfigured.
  // Story 8.2 (ADO-2011), AC6: a `notAvailable` (discontinued exact hit) row has no
  // live stock to show, so it is left out of these reads.
  const liveSkus = (result?.products ?? [])
    .filter((p) => p.notAvailable !== true)
    .map((p) => p.sku);
  useAvailability(liveSkus);
  // Pricing migration 17 (ADO-2248): one price request for the same products (no-op unless
  // VITE_REAL_PRICES_ENABLED is on).
  usePrices(liveSkus);
  // Pricing migration 32 (ADO-3985): the same products' multi-buy deals, one request (D-5: no-op
  // unless VITE_REAL_PRICES_ENABLED is on).
  useMultibuyDeals(liveSkus);
  // Story 8.12 (ADO-2021): which of them the Customer already has on order, one batched read.
  useOnOrder(liveSkus);

  // Story 7.29 (ADO-2025), D-7.29-29: the chips and the active-filter count read the selection the
  // last search applied, never a member it ignored. The echo stands only for the selection it
  // answered: before the first result, and while the selection differs from that one (a toggle or
  // a chip removal in flight, or a search that failed), the raw selection shows.
  const answeredForAccount = result !== null && answeredAccountCode === catalogueAccountCode;
  const appliedFilters =
    answeredForAccount &&
    result.appliedFilters !== undefined &&
    answeredFiltersKey === filtersKeyOf(activeFilters)
      ? result.appliedFilters
      : activeFilters;
  const filtersActive = hasActiveFilters(appliedFilters);
  const narrowed = filtersActive || collectedOffers;
  // Story 7.35 (ADO-2032): route a facet mutation to the host (URL) when
  // controlled, otherwise mutate local page-mode state. Each computes the next
  // selection from the latest one (`latestFiltersRef`), so both paths share one reducer.
  const applyFilters = (next: ProductFilters) => {
    latestFiltersRef.current = next;
    if (controlled) onFiltersChange?.(next);
    else setFilters(next);
  };
  const toggleValue = (key: FacetKey, value: string) =>
    applyFilters(toggleCoupledFilterValue(latestFiltersRef.current, key, value));
  // Story 7.31 (ADO-1113): clearing the filters also unticks "On Offer", leaving the collection.
  const clearAll = () => {
    applyFilters({});
    if (collectedOffers) onCollectedOffersChange?.(false);
  };
  const collectedOffersRail =
    onCollectedOffersChange === undefined
      ? undefined
      : { on: collectedOffers, onChange: onCollectedOffersChange };
  const removeChip = (key: FacetKey, value: string) =>
    applyFilters(removeCoupledFilterValue(latestFiltersRef.current, key, value));

  // The count caption survives an error too — the retained grid/empty-state
  // below the banner must keep saying WHICH kind of set it belongs to (the
  // toolbar crumb carries the term itself). Zero results still echo the query.
  const statusText =
    phase === 'loading'
      ? 'Searching…'
      : (phase === 'ready' || phase === 'error') && result !== null
        ? result.isEmpty
          ? `No results for “${result.query}”`
          : `Showing ${result.products.length} ${
              result.products.length === 1 ? 'result' : 'results'
            }`
        : '';

  const filterPanel = (
    <FilterPanel
      facets={result?.facets ?? []}
      narrowings={answeredForAccount ? (result.narrowings ?? []) : []}
      unavailableNarrowings={answeredForAccount ? (result.unavailableNarrowings ?? []) : []}
      selected={activeFilters}
      onToggleValue={toggleValue}
      onClearAll={clearAll}
      hasActive={narrowed}
      collectedOffers={collectedOffersRail}
    />
  );

  const content = (
    <div className={styles.search}>
      {!embedded && (
        <form className={styles.searchBar} role="search" onSubmit={onSubmit}>
          <FormField
            label="Search products"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, description or SKU…"
            maxLength={MAX_SEARCH_QUERY_LENGTH}
          />
          <Button type="submit">Search</Button>
        </form>
      )}

      {/* Re-skinned listing shell (ADO-903): pinned filters rail on the LEFT
          (desktop, same CollapsibleFilterSidebar + ad placeholder as the
          category listing), results content on the RIGHT. */}
      <div className={listing.shell}>
        <CollapsibleFilterSidebar open={showFilters} className={listing.sidebar} showAdPlaceholder>
          <div id="search-filter-panel">{filterPanel}</div>
        </CollapsibleFilterSidebar>

        <div className={listing.content}>
          {/* Toolbar (prototype "Listing options" row): Filters switch (desktop) /
              Filters drawer button (<1024) · breadcrumb-style Shop / “term” crumb ·
              result count + grid/list view toggle. */}
          <div className={listing.toolbar} role="group" aria-label="Listing options">
            <button
              type="button"
              className={listing.filtersToggle}
              aria-expanded={showFilters}
              aria-controls="search-filter-panel"
              onClick={toggleFilters}
            >
              Filters
              <span
                className={`${listing.filtersSwitch} ${showFilters ? listing.filtersSwitchOn : ''}`}
                aria-hidden
              >
                <span className={listing.filtersKnob} />
              </span>
            </button>
            <button
              type="button"
              className={listing.drawerButton}
              aria-haspopup="dialog"
              onClick={() => setDrawerOpen(true)}
            >
              <Icon name="sliders" size={12} />
              Filters
            </button>

            <div className={listing.toolbarCrumb}>
              {runTerm !== '' && (
                // Breadcrumb-style Shop / “term” crumb (prototype search-results
                // chrome). "Shop" is a non-link crumb here — leaving the search is
                // the header's Shop link/Clear control, not this trail.
                <nav aria-label="Breadcrumb" className={crumbs.breadcrumb}>
                  <ol className={crumbs.list}>
                    <li className={crumbs.item}>
                      <span className={crumbs.current}>Shop</span>
                    </li>
                    <li className={`${crumbs.item} ${crumbs.itemCurrent}`}>
                      <Icon name="caretRight" size={16} className={crumbs.separator} />
                      <span className={crumbs.current} aria-current="page">
                        “{runTerm}”
                      </span>
                    </li>
                  </ol>
                </nav>
              )}
            </div>

            <div className={listing.toolbarRight}>
              {/* The ONE live region on the page (besides the error Banner's own
                  role="alert") — persistently mounted so screen readers reliably
                  announce its text changes; the empty-state box below is visual only. */}
              <p className={listing.count} role="status">
                {statusText}
              </p>
              <div className={listing.viewToggle} role="group" aria-label="View mode">
                <button
                  type="button"
                  className={`${listing.viewButton} ${viewMode === 'grid' ? listing.viewButtonActive : ''}`}
                  aria-label="Grid view"
                  aria-pressed={viewMode === 'grid'}
                  onClick={() => selectView('grid')}
                >
                  <Icon name="gridView" size={20} />
                </button>
                <button
                  type="button"
                  className={`${listing.viewButton} ${viewMode === 'list' ? listing.viewButtonActive : ''}`}
                  aria-label="List view"
                  aria-pressed={viewMode === 'list'}
                  onClick={() => selectView('list')}
                >
                  <Icon name="listView" size={20} />
                </button>
              </div>
            </div>
          </div>

          <FilterChips
            filters={appliedFilters}
            onRemove={removeChip}
            onClearAll={clearAll}
            scopeChips={
              collectedOffersRail?.on === true
                ? [{ label: 'On Offer', onRemove: () => collectedOffersRail.onChange(false) }]
                : undefined
            }
            className={listing.chipsRow}
          />

          {/* Story 7-FU-C (ADO-1216), AC6 — when the result set was capped by the
              server search limit, say so honestly instead of presenting the shown
              rows as the complete set. Only meaningful once a result is on screen
              (ready/error keep the last good result); never on the loading/idle path.
              Gated on `!isEmpty`: `truncated` reflects the raw DB cap BEFORE the
              in-memory facet filter (AC4), so an active facet can empty the display
              while `truncated` stays true — showing this notice beside the "No
              results" empty state would contradict it. The copy is count-free: the
              client has only the `truncated` boolean, not the exact cap (a `total`
              is deferred — D-4/OQ-4), so it must not assert "the first N matches"
              (N would be the post-facet displayed count, not the cap). */}
          {(phase === 'ready' || phase === 'error') &&
            result !== null &&
            result.truncated &&
            !result.isEmpty && (
              <p className={styles.truncationNotice} role="status">
                There are more matches than shown here — refine your search to narrow it down.
              </p>
            )}

          <div className={listing.main}>
            {phase === 'idle' && !embedded && (
              <p className={styles.prompt}>
                Start typing a product name, description or SKU to search.
              </p>
            )}

            {phase === 'error' && (
              <Banner type="error">Couldn&rsquo;t run your search. Please try again.</Banner>
            )}

            {collectedOffers &&
              result?.unavailableOfferKinds?.includes('FEATURED_LINE') === true && (
                // Story 7.32 (ADO-1115): as on the collected-offers listing, the Featured Lines
                // could not be read this time; the other offers matching the term are listed.
                <Banner type="warning">
                  Featured Lines can&rsquo;t be loaded right now. Everything else on offer is shown.
                </Banner>
              )}

            {result !== null && result.isEmpty && (
              // AC3/AC4 — explicit empty state echoing the query, not an error.
              // When filters are active, offer a clear-filters affordance — and
              // say the FILTERS excluded the matches (the term itself did match),
              // mirroring Browse's dedicated title (code-review 2026-07-13).
              // "On Offer" narrows the search as a filter does (Story 7.31, ADO-1113).
              <div className={styles.empty}>
                <p className={styles.emptyTitle}>
                  {narrowed
                    ? `No products for “${result.query}” match the selected filters.`
                    : `No products match “${result.query}”.`}
                </p>
                <p className={styles.emptyHint}>
                  {narrowed
                    ? 'Try removing a filter to widen your results.'
                    : 'Try a different spelling, fewer words, or a product code.'}
                </p>
                {narrowed && (
                  <Button variant="secondary" onClick={clearAll}>
                    Clear filters
                  </Button>
                )}
              </div>
            )}

            {result !== null && !result.isEmpty && (
              <ul className={viewMode === 'list' ? listing.list : listing.grid}>
                {result.products.map((product, index) => (
                  <li key={product.id}>
                    {/* Story 7.9 (ADO-1141) filled the 7.2-AC7 add-to-basket seam:
                        the shared ProductCard now carries the one-click "Add to Basket"
                        button (optimistic client store; durable write is Story 8.2, D-1). */}
                    <ProductCard
                      product={product}
                      priority={index < EAGER_IMAGE_COUNT}
                      layout={viewMode}
                    />
                  </li>
                ))}
              </ul>
            )}

            {/* No pagination footer: search is a single capped page (cursor paging
                is a Browse concern; the truncation notice above covers the cap). */}
          </div>
        </div>
      </div>

      {/* Mobile/tablet (<1024px) filters drawer — the SAME panel, in the shared
          left drawer (prototype parity with the category listing). */}
      <FiltersDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        {filterPanel}
      </FiltersDrawer>
    </div>
  );

  // Embedded under the /products SiteHeader (which owns the search box + depot
  // switcher): return just the results body. Page mode keeps the AppShell chrome.
  if (embedded) return content;

  return (
    <AppShell
      title="Search"
      subtitle="Find products by name, description or SKU."
      actions={<DepotPicker />}
    >
      {content}
    </AppShell>
  );
}

export default ProductSearch;
